import { useState, useEffect } from "react";
import { Copy, Check } from "lucide-react";
import { FacebookIcon, XLogoIcon, InstagramIcon, WhatsappIcon } from "../lib/icons";
import { toast } from "sonner";

/**
 * Peer-to-peer share bar for individual campaign pages.
 *
 * Pre-populates the share text with:
 *   - Student first name
 *   - School
 *   - Current funding percentage
 *   - Direct campaign URL
 *
 * Platforms:
 *   - Facebook: sharer.php (FB reads Open Graph tags from the URL itself)
 *   - X:        twitter.com/intent/tweet (prefills text + url)
 *   - WhatsApp: api.whatsapp.com/send (prefills text; auto-appends URL)
 *   - Instagram: no direct-share web URL exists. On mobile, invokes the native
 *     Web Share sheet (which surfaces Instagram if installed). On desktop, we
 *     copy the link with an instructional toast — the same fallback used by
 *     Kickstarter, GoFundMe, and Patreon.
 *   - Copy: Clipboard API + toast.
 *
 * Visual: ink-outline circular buttons, subtle, editorial. Matches the site's
 * ink-filled twin CTA system.
 */

const IconButton = ({ label, onClick, children, testid, bold }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    title={label}
    data-testid={testid}
    className={bold
      ? "flex h-12 w-12 items-center justify-center rounded-full bg-ink text-sand transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40 focus-visible:ring-offset-2 focus-visible:ring-offset-yellow"
      : "group flex h-10 w-10 items-center justify-center rounded-full border border-ink/20 text-ink transition-colors hover:border-ink hover:bg-ink hover:text-sand focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/40"}
  >
    {children}
  </button>
);

/**
 * @param campaign      - existing campaign object (name, school, goal). Optional
 *                        when `shareText` is provided directly.
 * @param raised        - amount raised (used to compute funding %).
 * @param shareText     - explicit share text override (used by the just-launched
 *                        wizard success flow where the campaign has no funding
 *                        history yet).
 * @param shareUrl      - explicit URL override (default: window.location.href).
 * @param label         - eyebrow label above the buttons.
 */
export const CampaignShareBar = ({
  campaign,
  raised,
  shareText: shareTextProp,
  shareUrl,
  label = "Share this campaign",
  bold = false,
}) => {
  const [copied, setCopied] = useState(false);
  const [url, setUrl] = useState("");

  useEffect(() => {
    if (typeof window !== "undefined") {
      setUrl(shareUrl || window.location.href);
    }
  }, [shareUrl]);

  // Component supports two modes:
  //   1. Live campaign (default) — reads name/school/pct from `campaign`
  //   2. Just-launched / preview — copy is supplied via `shareText` prop
  if (!campaign && !shareTextProp) return null;

  const pct = campaign
    ? Math.min(100, Math.round((raised / campaign.goal) * 100))
    : 0;
  const firstName = campaign
    ? String(campaign.name || "").split(" ")[0].replace(/\.$/, "")
    : "";
  const shareText =
    shareTextProp ??
    (`${firstName} from ${campaign.school} is ${pct}% funded on My Rich Aunt. ` +
      `Help ${firstName} reach the goal.`);

  const openShare = (href) => {
    window.open(href, "_blank", "noopener,noreferrer,width=640,height=640");
  };

  const shareFacebook = () =>
    openShare(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`);

  const shareX = () =>
    openShare(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`
    );

  const shareWhatsApp = () =>
    openShare(
      `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} ${url}`)}`
    );

  const shareInstagram = async () => {
    // Native share sheet on mobile → Instagram is available if installed.
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: `${firstName} on My Rich Aunt`, text: shareText, url });
        return;
      } catch (e) {
        // user cancelled or share failed — fall through to copy-link fallback
      }
    }
    // Desktop fallback: copy link + instruct
    try { await navigator.clipboard.writeText(url); } catch (e) { /* ignore */ }
    toast.success("Link copied", {
      description: "Open Instagram and paste it in your Story or bio.",
    });
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(url); } catch (e) { /* ignore */ }
    setCopied(true);
    toast.success("Link copied", {
      description: "Share it anywhere to help reach the goal faster.",
    });
    setTimeout(() => setCopied(false), 1600);
  };

  if (bold) {
    const icon = "h-5 w-5";
    return (
      <div className="flex flex-wrap items-center gap-3" data-testid="campaign-share-bar">
        <IconButton bold label="Share on Facebook" onClick={shareFacebook} testid="share-facebook"><FacebookIcon className={icon} /></IconButton>
        <IconButton bold label="Share on X (formerly Twitter)" onClick={shareX} testid="share-x"><XLogoIcon className={icon} /></IconButton>
        <IconButton bold label="Share to Instagram Story" onClick={shareInstagram} testid="share-instagram"><InstagramIcon className={icon} /></IconButton>
        <IconButton bold label="Share on WhatsApp" onClick={shareWhatsApp} testid="share-whatsapp"><WhatsappIcon className={icon} /></IconButton>
        <button type="button" onClick={copyLink} data-testid="share-copy" className="inline-flex h-12 items-center gap-2 rounded-full border-2 border-ink px-5 text-base font-semibold text-ink transition-colors hover:bg-ink hover:text-sand">
          {copied ? <Check className="h-4 w-4" strokeWidth={2} /> : <Copy className="h-4 w-4" strokeWidth={2} />} Copy link
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-2" data-testid="campaign-share-bar">
      <div className="flex items-center gap-4">
        {/* Compact horizontal palette-oval cluster — matches DoodleDivider dots
            without the flanking hairlines (this sits inline with a label). */}
        <div
          aria-hidden="true"
          data-testid="share-bar-doodle"
          className="flex items-center gap-2"
        >
          <span className="h-2 w-5 rounded-full bg-tangerine" />
          <span className="h-2 w-5 rounded-full bg-cobalt" />
          <span className="h-2 w-5 rounded-full bg-grass" />
          <span className="h-2 w-5 rounded-full bg-blossom" />
          <span className="h-2 w-5 rounded-full bg-yellow" />
        </div>
        <p className="text-[13px] font-medium uppercase tracking-[0.16em] text-olive">
          {label}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <IconButton label="Share on Facebook" onClick={shareFacebook} testid="share-facebook">
          <FacebookIcon className="h-4 w-4" />
        </IconButton>
        <IconButton label="Share on X (formerly Twitter)" onClick={shareX} testid="share-x">
          <XLogoIcon className="h-4 w-4" />
        </IconButton>
        <IconButton label="Share to Instagram Story" onClick={shareInstagram} testid="share-instagram">
          <InstagramIcon className="h-4 w-4" />
        </IconButton>
        <IconButton label="Share on WhatsApp" onClick={shareWhatsApp} testid="share-whatsapp">
          <WhatsappIcon />
        </IconButton>
        <IconButton label="Copy link" onClick={copyLink} testid="share-copy">
          {copied ? (
            <Check className="h-4 w-4" strokeWidth={2} />
          ) : (
            <Copy className="h-4 w-4" strokeWidth={1.75} />
          )}
        </IconButton>
      </div>
    </div>
  );
};
